import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { DiagnosticStatus, labelsByDiagnosticStatus } from '../../../shared/models/enums/diagnostic-status.enum';
import { Title } from '../../../shared/models/enums/title.enum';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { DiagnosticService } from '../../../shared/services/diagnostic.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { DiagnosticDetailsComponent } from './diagnostic-details/diagnostic-details.component';
import { DiagnosticInit } from './diagnosticInit';
import { DisplayDiagnosticDto } from './displayDiagnosticDto';

@Component({
  selector: 'app-diagnostic',
  templateUrl: './diagnostic.component.html',
})
export class DiagnosticComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  public user: UserDto;
  public readonly diagnosticStatusMap: Map<DiagnosticStatus, string> = labelsByDiagnosticStatus;
  private static readonly resetDiagnosticIer: Pagination<DisplayDiagnosticDto> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));

  constructor(
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly diagnosticService: DiagnosticService,
    private readonly beneficiariesService: BeneficiariesService,
  ) {
    super();
  }

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public diagnosticsIER: Pagination<DisplayDiagnosticDto> = DiagnosticComponent.resetDiagnosticIer;

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.updateDiagnosticsIER(this.diagnosticsIER.options);

    this.diagnosticService.diagnosticCreated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.reloadDiagnostics();
    });
  }

  private async reloadDiagnostics(): Promise<void> {
    this.diagnosticsIER = await this.diagnosticService.findDiagnostics(this.beneficiary.id, this.diagnosticsIER.options);
  }

  private async updateDiagnosticsIER(pagination: PaginationOptions): Promise<void> {
    this.diagnosticsIER = await this.diagnosticService.findDiagnostics(this.beneficiary.id, pagination);
  }

  private async openModal(diagnosticId?: number, diagnosticInit?: DiagnosticInit): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(DiagnosticDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: DiagnosticDetailsComponent = modal.componentInstance as DiagnosticDetailsComponent;
    await modalComponent.onInit(this.beneficiary.id, diagnosticId, diagnosticInit);
  }

  public async openCreateModal(): Promise<void> {
    if (this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'TERRITORY'].includes(group))) {
      try {
        const diagnosticInit: DiagnosticInit = await this.diagnosticService.findByBeneficiaryId(this.beneficiary.id);

        this.openModal(undefined, diagnosticInit);
      } catch (error) {
        if (error.status === 461) {
          this.toastService.show('Veuillez compléter les informations obligatoires des onglets Complément et Emploi.', 'danger');
        } else if (error.status === 462) {
          this.toastService.show('Veuillez sélectionner une civilité différente de « Inconnu » pour le bénéficiaire.', 'danger');
        } else if (error.status === 463) {
          this.toastService.show('Le référent du bénéficiaire doit avoir au moins une adresse valide.', 'danger');
        } else if (error.status === 500) {
          this.toastService.show("Le bénéficaire n'a pas de référent enregistré", 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async openConsultModal(diagnosticId: number): Promise<void> {
    if (this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+', 'TERRITORY'].includes(group))) {
      try {
        if (!this.beneficiary.title || this.beneficiary.title === ('Unknown' as Title)) {
          throw new Error('Unknown title.');
        }
        this.openModal(diagnosticId);
      } catch (error) {
        if (error.status === 461) {
          this.toastService.show('Veuillez compléter les informations obligatoires des onglets Complément et Emploi.', 'danger');
        } else if (error.message === 'Unknown title.') {
          this.toastService.show('Veuillez sélectionner une civilité différente de « Inconnu » pour le bénéficiaire.', 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateDiagnosticsIER(this.diagnosticsIER.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateDiagnosticsIER(this.diagnosticsIER.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event && event.target && event.target.value) {
      const limit: number = event.target.value;
      await this.updateDiagnosticsIER(this.diagnosticsIER.options.withLimit(limit));
    }
  }
}
